/**
 * WanWatchdogCard — the site's gateway connectivity-remediation policy.
 *
 * Site-scoped on purpose: probe hosts are site infrastructure, and a gateway
 * failover PAIR must share one policy. Applying fans out to every gateway at the
 * site (APs are untouched — `wifi`/`leds` is an access point's playbook and does
 * nothing for a gateway).
 *
 * TWO THINGS DRIVE THIS DESIGN:
 *
 * 1. The ladder is a SEQUENCE, not a set. The firmware runs the enabled rungs in
 *    order, one rung per `escalate_every` further failed checks. So it is drawn as
 *    a numbered ladder with a connector rail, and the number on each rung is the
 *    stage it will ACTUALLY fire at (its index among the *enabled* rungs) — not
 *    its position in the catalog. Toggling a rung renumbers the ones below it,
 *    which is the honest representation of what the operator just changed.
 *
 * 2. `watchdog-reset` REBOOTS THE HARDWARE. That fact has to be impossible to
 *    miss, so it escalates in form as well as in label: the rungs run
 *    neutral -> warn -> critical, and enabling the last one raises a persistent
 *    banner. Severity colour here is operational state, never decoration.
 *
 * Guardrail errors (HTTP 400) are rendered VERBATIM: the server names the exact
 * host and why it can never answer, which is far more useful than any message we
 * could invent client-side. See services/wan_watchdog.py for why those guardrails
 * exist — a policy whose probes can never succeed makes every gateway that
 * received it remediate forever.
 */
import { useMemo, useState } from "react";
import {
  useSiteWanWatchdog,
  useSaveSiteWanWatchdog,
  useClearSiteWanWatchdog,
} from "@/lib/api/hooks";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import type {
  WatchdogAction,
  WatchdogPolicy,
  WatchdogPolicyInput,
} from "@/types/domain";

/** Copy for each rung: what it does and how bad it is. Order = escalation order. */
const RUNGS: {
  id: WatchdogAction;
  label: string;
  effect: string;
  severity: "neutral" | "warn" | "critical";
}[] = [
  {
    id: "dhcp-renew",
    label: "Renew DHCP lease",
    effect: "Nudges the DHCP client. Fixes a stale lease after the uplink reboots.",
    severity: "neutral",
  },
  {
    id: "network-restart",
    label: "Restart networking",
    effect: "Restarts netifd. Clears wedged interface or driver state. Drops traffic briefly.",
    severity: "warn",
  },
  {
    id: "watchdog-reset",
    label: "Reboot the gateway",
    effect: "Hardware watchdog reset. The site is offline until the gateway boots.",
    severity: "critical",
  },
];

/** Sensible new-policy defaults: detect and self-heal, but do NOT reboot unasked. */
const DEFAULTS: WatchdogPolicyInput = {
  ping_hosts: ["1.1.1.1", "8.8.8.8"],
  download_hosts: [],
  check_interval: 60,
  check_threshold: 3,
  actions: ["dhcp-renew", "network-restart"],
  escalate_every: 1,
  reset_budget: 3,
  reset_window: 3600,
};

const linesToList = (s: string): string[] =>
  s
    .split(/[\s,]+/)
    .map((h) => h.trim())
    .filter(Boolean);

/** Coerce the device's stringly-typed config-raw values back to numbers. */
const num = (v: string | number | null | undefined, fallback: number): number => {
  const n = typeof v === "string" ? parseInt(v, 10) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : fallback;
};

function policyToInput(p: WatchdogPolicy): WatchdogPolicyInput {
  return {
    ping_hosts: p.ping_hosts ?? [],
    download_hosts: p.download_hosts ?? [],
    check_interval: num(p.check_interval, DEFAULTS.check_interval),
    check_threshold: num(p.check_threshold, DEFAULTS.check_threshold),
    actions: (p.actions ?? []).filter((a) => RUNGS.some((r) => r.id === a)),
    escalate_every: num(p.escalate_every, DEFAULTS.escalate_every),
    reset_budget: num(p.reset_budget, DEFAULTS.reset_budget),
    reset_window: num(p.reset_window, DEFAULTS.reset_window),
  };
}

export function WanWatchdogCard({ siteId }: { siteId: string }) {
  const { data, isLoading, error } = useSiteWanWatchdog(siteId);
  const save = useSaveSiteWanWatchdog(siteId);
  const clear = useClearSiteWanWatchdog(siteId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<WatchdogPolicyInput>(DEFAULTS);

  const gateways = data?.gateways ?? [];
  const configured = gateways.filter((g) => g.policy);
  // The live policy to show/seed from: the first gateway that actually has one.
  const live = configured[0]?.policy ?? null;
  const anyReboots = configured.some((g) => g.policy?.resets_on_failure);

  const beginEdit = () => {
    setDraft(live ? policyToInput(live) : DEFAULTS);
    save.reset();
    setEditing(true);
  };
  const cancelEdit = () => {
    save.reset();
    setEditing(false);
  };

  if (isLoading) {
    return (
      <li className="rounded-md border border-line p-4 text-sm text-ink-muted">
        Loading WAN watchdog…
      </li>
    );
  }
  // A site with no gateways has nothing to apply this to — say so rather than
  // offering a form that could never push anywhere.
  if (!error && gateways.length === 0) {
    return (
      <li className="rounded-md border border-line p-4">
        <div className="font-medium text-ink">WAN watchdog</div>
        <p className="mt-0.5 text-sm text-ink-muted">
          Automatic recovery when the site&rsquo;s internet path fails.
        </p>
        <p className="mt-1 text-xs text-ink-subtle">
          No gateway in this site yet — the watchdog runs on gateways, not access points.
        </p>
      </li>
    );
  }

  return (
    <li className="rounded-md border border-line p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-ink">WAN watchdog</span>
            <CoverageChip configured={configured.length} total={gateways.length} />
          </div>
          <p className="mt-0.5 text-sm text-ink-muted">
            Detects a dead internet path — a lease that still looks valid but carries no
            traffic — and escalates through recovery steps until it comes back.
          </p>
          <p className="mt-1 text-xs text-ink-subtle">
            {configured.length} of {gateways.length} gateway
            {gateways.length === 1 ? "" : "s"} configured
          </p>
        </div>

        {/* org_admin (OP#1580): the policy can reboot every gateway at the site, so the
            server refuses a site_operator write (PUT/DELETE /sites/<id>/wan-watchdog). */}
        <RoleGate
          min="org_admin"
          mode="hide"
          fallback={<span className="shrink-0 text-xs text-ink-subtle">customer admin</span>}
        >
          {!editing && (
            <Button variant="secondary" onClick={beginEdit}>
              {live ? "Edit policy" : "Set up"}
            </Button>
          )}
        </RoleGate>
      </div>

      {/* The one fact that must never be missed. */}
      {anyReboots && !editing && (
        <p className="mt-3 flex items-start gap-2 rounded border border-critical/40 bg-critical/5 px-2 py-1.5 text-xs text-critical">
          <span aria-hidden="true">&#9888;</span>
          <span>
            This policy can <strong className="font-semibold">reboot the gateway</strong>. The
            site goes offline while it restarts.
          </span>
        </p>
      )}

      {error && (
        <p className="mt-3 text-xs text-critical">
          Could not load the watchdog policy: {error.message}
        </p>
      )}

      {/* Applied state, per gateway — a pair must agree, so show both. */}
      {!editing && gateways.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1.5">
          {gateways.map((g) => (
            <li
              key={g.serial}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 rounded bg-surface-2 px-2 py-1.5 text-xs"
            >
              <span className="font-medium text-ink">{g.name}</span>
              {g.policy ? (
                <span className="text-ink-muted">
                  every{" "}
                  <span className="font-mono tabular-nums">{g.policy.check_interval ?? "—"}s</span>
                  , after{" "}
                  <span className="font-mono tabular-nums">
                    {g.policy.check_threshold ?? "—"}
                  </span>{" "}
                  failed checks &middot;{" "}
                  {g.policy.actions.length ? (
                    g.policy.actions.map((a, i) => (
                      <span key={a}>
                        {i > 0 && <span className="text-ink-subtle"> &rarr; </span>}
                        <span className={a === "watchdog-reset" ? "text-critical" : undefined}>
                          {RUNGS.find((r) => r.id === a)?.label ?? a}
                        </span>
                      </span>
                    ))
                  ) : (
                    <span className="text-ink-subtle">detect only</span>
                  )}
                </span>
              ) : (
                <span className="text-ink-subtle">not configured</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Result of the last fan-out. */}
      {!editing && save.data && (
        <p className="mt-2 text-xs text-healthy">
          Pushed to {save.data.pushed} gateway{save.data.pushed === 1 ? "" : "s"}
          {save.data.skipped ? `, ${save.data.skipped} already set` : ""}
          {save.data.errors ? `, ${save.data.errors} failed` : ""}.
        </p>
      )}
      {!editing && clear.data && (
        <p className="mt-2 text-xs text-healthy">
          Removed from {clear.data.pushed} gateway{clear.data.pushed === 1 ? "" : "s"}.
        </p>
      )}

      {editing && (
        <PolicyEditor
          draft={draft}
          setDraft={setDraft}
          hasLive={!!live}
          saving={save.isPending}
          clearing={clear.isPending}
          /* The server's guardrail text names the offending host and why — show it as-is. */
          saveError={save.error?.message ?? null}
          onSave={() => save.mutate(draft, { onSuccess: () => setEditing(false) })}
          onClear={() => clear.mutate(undefined, { onSuccess: () => setEditing(false) })}
          onCancel={cancelEdit}
        />
      )}
    </li>
  );
}

/* ------------------------------------------------------------------ */

function PolicyEditor({
  draft,
  setDraft,
  hasLive,
  saving,
  clearing,
  saveError,
  onSave,
  onClear,
  onCancel,
}: {
  draft: WatchdogPolicyInput;
  setDraft: (p: WatchdogPolicyInput) => void;
  hasLive: boolean;
  saving: boolean;
  clearing: boolean;
  saveError: string | null;
  onSave: () => void;
  onClear: () => void;
  onCancel: () => void;
}) {
  const set = <K extends keyof WatchdogPolicyInput>(k: K, v: WatchdogPolicyInput[K]) =>
    setDraft({ ...draft, [k]: v });

  const toggleRung = (id: WatchdogAction) => {
    const on = draft.actions.includes(id);
    // Always re-sort into catalog order: the stored order IS the escalation order,
    // so it must not depend on which rung the operator happened to click first.
    const next = on
      ? draft.actions.filter((a) => a !== id)
      : RUNGS.filter((r) => r.id === id || draft.actions.includes(r.id)).map((r) => r.id);
    set("actions", next);
  };

  const reboots = draft.actions.includes("watchdog-reset");
  const noProbes = draft.ping_hosts.length === 0 && draft.download_hosts.length === 0;

  // Worst case before the last rung fires — the number an operator actually wants.
  const worstCase = useMemo(() => {
    if (draft.actions.length === 0) return null;
    const checksToLast =
      draft.check_threshold + (draft.actions.length - 1) * Math.max(1, draft.escalate_every);
    return checksToLast * draft.check_interval;
  }, [draft.actions.length, draft.check_threshold, draft.escalate_every, draft.check_interval]);

  return (
    <div className="mt-3 flex flex-col gap-4 rounded border border-line bg-surface-2 p-3">
      {/* --- Detection --- */}
      <section>
        <SectionLabel>Detection</SectionLabel>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          <Field
            label="Ping hosts"
            hint="One per line. Use a neutral public host, not only this controller."
          >
            <textarea
              className="h-16 w-full resize-y rounded border border-line bg-surface px-2 py-1 font-mono text-xs text-ink"
              value={draft.ping_hosts.join("\n")}
              onChange={(e) => set("ping_hosts", linesToList(e.target.value))}
              spellCheck={false}
            />
          </Field>
          <Field
            label="Download hosts"
            hint="Optional. Fetches /online.txt and requires the body to be “ok”, which catches a captive portal."
          >
            <textarea
              className="h-16 w-full resize-y rounded border border-line bg-surface px-2 py-1 font-mono text-xs text-ink"
              value={draft.download_hosts.join("\n")}
              onChange={(e) => set("download_hosts", linesToList(e.target.value))}
              spellCheck={false}
            />
          </Field>
          <NumField
            label="Check every"
            unit="seconds"
            min={10}
            value={draft.check_interval}
            onChange={(v) => set("check_interval", v)}
          />
          <NumField
            label="Failed checks before acting"
            unit="checks"
            min={1}
            value={draft.check_threshold}
            onChange={(v) => set("check_threshold", v)}
          />
        </div>
        {noProbes && (
          <p className="mt-2 text-xs text-warn">At least one probe host is required.</p>
        )}
      </section>

      {/* --- The ladder --- */}
      <section>
        <SectionLabel>Recovery ladder</SectionLabel>
        <p className="mt-1 text-xs text-ink-muted">
          Steps run in this order, one step per {draft.escalate_every}{" "}
          further failed check. The gateway stops as soon as the link returns.
        </p>
        <ol className="mt-2 flex flex-col">
          {RUNGS.map((rung) => {
            const enabled = draft.actions.includes(rung.id);
            // Stage number = position among ENABLED rungs, i.e. when it really fires.
            const stage = enabled ? draft.actions.indexOf(rung.id) + 1 : null;
            return (
              <Rung
                key={rung.id}
                rung={rung}
                enabled={enabled}
                stage={stage}
                onToggle={() => toggleRung(rung.id)}
              />
            );
          })}
        </ol>
        {draft.actions.length === 0 && (
          <p className="mt-2 text-xs text-ink-subtle">
            No recovery steps — the gateway will detect and log an outage but not act on it.
          </p>
        )}
        {worstCase !== null && (
          <p className="mt-2 text-xs text-ink-subtle">
            Last step fires after roughly{" "}
            <span className="font-mono tabular-nums text-ink-muted">
              {Math.round(worstCase / 60)} min
            </span>{" "}
            of continuous failure.
          </p>
        )}
        <div className="mt-3">
          <NumField
            label="Escalate after"
            unit="further failed checks"
            min={1}
            value={draft.escalate_every}
            onChange={(v) => set("escalate_every", v)}
          />
        </div>
      </section>

      {/* --- Reboot limits: only meaningful once the reboot rung is on --- */}
      {reboots && (
        <section className="rounded border border-critical/40 bg-critical/5 p-2.5">
          <p className="flex items-start gap-2 text-xs text-critical">
            <span aria-hidden="true">&#9888;</span>
            <span>
              <strong className="font-semibold">This policy can reboot the gateway.</strong> The
              site is offline while it restarts. The gateway will refuse to reboot if no probe has
              ever succeeded under this policy — a wrong probe host is treated as a
              misconfiguration, not an outage.
            </span>
          </p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <NumField
              label="Reboot at most"
              unit="times"
              min={1}
              value={draft.reset_budget}
              onChange={(v) => set("reset_budget", v)}
            />
            <NumField
              label="Within"
              unit="seconds"
              min={60}
              value={draft.reset_window}
              onChange={(v) => set("reset_window", v)}
            />
          </div>
          <p className="mt-1.5 text-xs text-critical/80">
            Past that, the gateway stays up in a degraded state so it remains reachable for
            diagnosis.
          </p>
        </section>
      )}

      {/* Guardrail refusals land here, verbatim from the server. */}
      {saveError && (
        <p className="rounded border border-critical/40 bg-critical/5 px-2 py-1.5 text-xs text-critical">
          {saveError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" disabled={saving || clearing || noProbes} onClick={onSave}>
          {saving ? "Applying…" : "Apply to all gateways"}
        </Button>
        <Button variant="ghost" disabled={saving || clearing} onClick={onCancel}>
          Cancel
        </Button>
        {hasLive && (
          <button
            type="button"
            disabled={saving || clearing}
            onClick={onClear}
            className="ml-auto text-xs text-ink-subtle underline underline-offset-2 hover:text-critical disabled:opacity-50"
          >
            {clearing ? "Removing…" : "Remove watchdog"}
          </button>
        )}
      </div>
    </div>
  );
}

/** One rung: number, rail, toggle. Severity escalates as you climb. */
function Rung({
  rung,
  enabled,
  stage,
  onToggle,
}: {
  rung: (typeof RUNGS)[number];
  enabled: boolean;
  stage: number | null;
  onToggle: () => void;
}) {
  // Static class strings only — Tailwind's scanner cannot see interpolated names,
  // so `text-${severity}` would silently ship without the colour ever being built.
  const labelTone = !enabled
    ? "text-ink-subtle"
    : rung.severity === "critical"
      ? "text-critical"
      : rung.severity === "warn"
        ? "text-warn"
        : "text-ink";
  return (
    <li className="flex gap-2.5">
      {/* Rail + stage marker. The rail is the sequence made visible. */}
      <div className="flex w-6 shrink-0 flex-col items-center">
        <span
          className={[
            "flex h-6 w-6 items-center justify-center rounded-full border text-[0.6875rem] font-semibold tabular-nums",
            enabled
              ? rung.severity === "critical"
                ? "border-critical bg-critical/10 text-critical"
                : rung.severity === "warn"
                  ? "border-warn bg-warn/10 text-warn"
                  : "border-line bg-surface text-ink"
              : "border-dashed border-line text-ink-subtle",
          ].join(" ")}
        >
          {stage ?? "–"}
        </span>
        {/* Connector: only between rungs, so the last one doesn't dangle.
            Indexed by id rather than by RUNGS[len-1] — noUncheckedIndexedAccess
            types that lookup as possibly-undefined. */}
        {rung.id !== "watchdog-reset" && (
          <span
            className={["w-px flex-1", enabled ? "bg-line" : "bg-line/50"].join(" ")}
            aria-hidden="true"
          />
        )}
      </div>

      <label className="flex flex-1 cursor-pointer items-start gap-2 pb-3">
        <input
          type="checkbox"
          className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-current"
          checked={enabled}
          onChange={onToggle}
        />
        <span className="min-w-0">
          <span className={`text-xs font-medium ${labelTone}`}>{rung.label}</span>
          <span className="block text-xs text-ink-subtle">{rung.effect}</span>
        </span>
      </label>
    </li>
  );
}

function CoverageChip({ configured, total }: { configured: number; total: number }) {
  if (configured === 0) {
    return (
      <span className="rounded border border-line px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-ink-subtle">
        Not set
      </span>
    );
  }
  if (configured < total) {
    return (
      <span className="rounded bg-warn/10 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-warn">
        Partial
      </span>
    );
  }
  return (
    <span className="rounded bg-healthy/10 px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-healthy">
      Active
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
      {children}
    </h3>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ink">{label}</span>
      {children}
      {hint && <span className="text-xs text-ink-subtle">{hint}</span>}
    </label>
  );
}

function NumField({
  label,
  unit,
  min,
  value,
  onChange,
}: {
  label: string;
  unit: string;
  min: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ink">{label}</span>
      <span className="flex items-center gap-1.5">
        <input
          type="number"
          min={min}
          className="w-20 rounded border border-line bg-surface px-2 py-1 text-xs tabular-nums text-ink"
          value={value}
          onChange={(e) => onChange(Math.max(min, parseInt(e.target.value, 10) || min))}
        />
        <span className="text-xs text-ink-subtle">{unit}</span>
      </span>
    </label>
  );
}
